import Button from "@/components/bricks/button/Button";
import { TextInputFormik } from "../../../../formcontrols/InputItem";
import DeleteIcon from "../../../../icons/DeleteIcon";
import { StickyFooter } from "../../../../stickyFooter/modalStickyFooter";
import { WizardReceipt } from "../../../../wizards/common/wizardReceipt";
import { useMailskabelon } from "./useMailskabelonModal";
import { ModalContext } from "../../../../Modal/Modal";
import { useContext, useState } from "react";
import { useEditor, Editor } from "@tiptap/react";
import {
  acceptedFileTypes,
  emailEditorConfig,
} from "../../../../../config/editorConfigs";
import EmailEditor from "../../../../sendEmail/EmailEditor";
import Vedhaeftelse from "../../../../sendEmail/Vedhaeftelse";
import { SelectFormik } from "../../../../formcontrols/Select";
import { del } from "../../../../../api/util";
import { convertEmailTypeFromForm } from "../../../../../util/email";
import AttachmentIcon from "../../../../icons/AttachmentIcon";
import InputErrorMessage from "../../../../InputErrorMessage";
import { SHARED_EMAILS } from "../../../../../constants/common";

export default function MailskabelonModal(props: {
  action: MailskabelonActionTypes;
  data?: MailskabelonDTO;
  onSuccess: () => void;
}) {
  const { action, data, onSuccess } = props;
  const { setModalOpen } = useContext(ModalContext);
  const [confirm, setConfirm] = useState(false);

  const formik = useMailskabelon(
    (data as MailskabelonFormik) || {
      afsender: "",
      emne: "",
      indhold: "",
      navn: "",
      type: "Person",
      vedhaeftelser: [],
    },
    onSuccess,
    setModalOpen,
    action,
    data?.id,
  );
  const editor = useEditor({
    ...emailEditorConfig,
    content: formik.values.indhold,
    onUpdate: (e) => {
      formik.setFieldValue("indhold", e.editor.getHTML());
    },
  }) as Editor;
  const { handleSubmit, isSubmitting } = formik;
  const onDelete = async () => {
    if (data?.id) {
      const result = await del<MailskabelonDTO>({
        query: `email/skabeloner/${data.id}`,
      });
      if (result.status >= 200 && result.status < 300) {
        setModalOpen(false);
        onSuccess();
      }
    }
  };

  return (
    <div className="c-modal-container">
      {!isSubmitting ? (
        <form onSubmit={handleSubmit}>
          <div className="c-item-list c-modal-container-inner">
            {" "}
            <div className="flex-100">
              <SelectFormik
                disabled={action === "rediger"}
                formik={formik}
                config={{
                  component: "select",
                  id: "type",
                  label: "Type",
                  placeholder: "Vælg type...",
                  locked: action === "rediger",
                }}
                data={
                  [
                    "Person",
                    "Firma",
                    "Kursus",
                    "Linje",
                    "Retssagsproeve",
                  ] as IEmailFormTypes[]
                }
              />
            </div>
            <div className="flex-100">
              <TextInputFormik
                formik={formik}
                config={{
                  component: "textinput",
                  id: "navn",
                  label: "Navn",
                  initialValue: "",
                  placeholder: "Angiv navn",
                }}
              />
            </div>
            <div className="flex-100">
              <SelectFormik
                formik={formik}
                config={{
                  component: "select",
                  id: "afsender",
                  label: "Afsender",
                  placeholder: "Vælg afsender email",
                }}
                data={SHARED_EMAILS.map((email) => ({
                  label: email.navn + " (" + email.email + ")",
                  value: email.email,
                }))}
              />
            </div>
            <div className="flex-100">
              <TextInputFormik
                formik={formik}
                config={{
                  component: "textinput",
                  id: "emne",
                  label: "Emne",
                  initialValue: "",
                  placeholder: "Angiv emne",
                }}
              />
            </div>
            <div className="flex-100">
              <label className="form-label">Indhold</label>
              <div className="position-relative d-flex flex-column h-100 overflow-hidden border rounded w-100 admin-editor">
                <EmailEditor
                  emailType={convertEmailTypeFromForm(formik.values.type)}
                  editor={editor}
                />
              </div>
            </div>
            <div className="flex-100">
              <label
                aria-label="Vedhæft filer"
                className="icon-bg btn--icon border-radius btn-secondary rounded"
                style={{ cursor: "pointer" }}
                color="secondary"
              >
                <AttachmentIcon />
                <input
                  name="files"
                  accept={acceptedFileTypes}
                  style={{ display: "none" }}
                  type="file"
                  id="input"
                  multiple
                  onChange={async (e) => {
                    const targetFiles = e.target.files;
                    if (targetFiles) {
                      const newFiles = Array.from(targetFiles);
                      const existingFileNames = formik.values.vedhaeftelser
                        ? formik.values.vedhaeftelser.map((f) =>
                            f instanceof File ? f.name : f.navn,
                          )
                        : [];
                      const uniqueNewFiles: IVedhaeftelse[] = newFiles
                        .filter((nf) => existingFileNames.indexOf(nf.name) < 0)
                        .map((f) => {
                          return {
                            navn: f.name,
                            mediaType: f.type,
                            dokument: f,
                          };
                        });
                      const files = formik.values.vedhaeftelser
                        ? [...formik.values.vedhaeftelser, ...uniqueNewFiles]
                        : uniqueNewFiles;
                      formik.setFieldValue("vedhaeftelser", files);
                    }
                  }}
                />
              </label>
            </div>
            <div className="flex-100 d-flex gap-1 flex-wrap mt-1">
              {formik.values.vedhaeftelser &&
                formik.values.vedhaeftelser.map((f, i) => {
                  return (
                    <Vedhaeftelse
                      key={i}
                      fil={f}
                      onRemove={() => {
                        const newFiles = formik.values.vedhaeftelser
                          ? formik.values.vedhaeftelser.filter((fil) => {
                              if ("dokument" in fil && "dokument" in f) {
                                return fil.navn !== f.navn;
                              } else if ("dokument" in fil || "dokument" in f) {
                                return true;
                              } else {
                                return fil.name !== f.name;
                              }
                            })
                          : [];
                        formik.setFieldValue("vedhaeftelser", newFiles);
                      }}
                    />
                  );
                })}
            </div>
          </div>
          {formik.status && (
            <InputErrorMessage
              className="text-center mb-2"
              value={formik.status}
            />
          )}
          <StickyFooter>
            {confirm ? (
              <>
                <Button
                  color="secondary"
                  type="button"
                  onClick={() => {
                    formik.setStatus(null);
                    setConfirm(false);
                  }}
                >
                  Afbryd
                </Button>
                <Button color="danger" type="button" onClick={() => onDelete()}>
                  Bekræft
                </Button>
              </>
            ) : (
              <>
                <Button
                  color="secondary"
                  type="button"
                  onClick={() => setModalOpen(false)}
                >
                  Annuller
                </Button>
                <div className="d-flex gap-4">
                  {action === "rediger" && (
                    <Button
                      color="secondary"
                      type="button"
                      onClick={() => {
                        formik.setStatus("Bekræft sletning af skabelon");
                        setConfirm(true);
                      }}
                    >
                      {<DeleteIcon width={14} />} Slet
                    </Button>
                  )}
                  <Button
                    type="submit"
                    color="primary"
                    disabled={formik.isSubmitting}
                  >
                    Gem
                  </Button>
                </div>
              </>
            )}
          </StickyFooter>
        </form>
      ) : (
        <>
          {action === "rediger" ? (
            <WizardReceipt
              heading="Skabelon er rettet"
              icon="success"
              // icon={loading ? "loading" : data ? "success" : "warning"}
            ></WizardReceipt>
          ) : (
            <WizardReceipt
              heading="Skabelon er oprettet"
              icon="success"
              // icon={loading ? "loading" : data ? "success" : "warning"}
            ></WizardReceipt>
          )}
          <div className="flex-100 d-flex justify-content-center mt-3">
            <Button color="secondary" onClick={() => setModalOpen(false)}>
              Luk
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
